import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format, parse, isValid } from "date-fns";
import { 
  User, Briefcase, GraduationCap, Award, Upload, FileText,
  CalendarIcon, AlertCircle, CheckCircle, Save, Send, X, Loader2, Image, Clock, Shield
} from "lucide-react";
import axios from "axios";
import { API, AuthContext } from "@/App";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// Custom Date Input Component with DD/MM/YYYY format
const DateInput = ({ value, onChange, error, setError }) => {
  const [inputValue, setInputValue] = useState("");
  const [localError, setLocalError] = useState("");

  // Initialize input value from date object
  useEffect(() => {
    if (value && isValid(value)) {
      setInputValue(format(value, "dd/MM/yyyy"));
      setLocalError("");
    }
  }, []);

  const validateAndParseDate = (dateStr) => {
    // Clear previous error
    setLocalError("");
    
    if (!dateStr || dateStr.trim() === "") {
      onChange(null);
      return;
    }

    // Check format DD/MM/YYYY
    const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
    const match = dateStr.match(dateRegex);
    
    if (!match) {
      setLocalError("Please enter date in DD/MM/YYYY format");
      return;
    }

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);

    // Validate day
    if (day < 1 || day > 31) {
      setLocalError("Day must be between 1 and 31");
      return;
    }

    // Validate month
    if (month < 1 || month > 12) {
      setLocalError("Month must be between 1 and 12");
      return;
    }

    // Validate year (reasonable range for DOB)
    const currentYear = new Date().getFullYear();
    if (year < 1900 || year > currentYear) {
      setLocalError(`Year must be between 1900 and ${currentYear}`);
      return;
    }

    // Parse the date
    const parsedDate = parse(dateStr, "dd/MM/yyyy", new Date());
    
    if (!isValid(parsedDate)) {
      setLocalError("Invalid date. Please check the values.");
      return;
    }

    // Check if date is not in the future
    if (parsedDate > new Date()) {
      setLocalError("Date of birth cannot be in the future");
      return;
    }

    // Check if the parsed date matches input (catches invalid dates like 31/02/2000)
    const formattedBack = format(parsedDate, "dd/MM/yyyy");
    const normalizedInput = `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    if (formattedBack !== normalizedInput) {
      setLocalError("Invalid date. Please check the day for this month.");
      return;
    }

    // Valid date
    onChange(parsedDate);
  };

  const handleInputChange = (e) => {
    let val = e.target.value;
    
    // Auto-insert slashes for better UX
    const digits = val.replace(/\D/g, '');
    if (digits.length <= 2) {
      val = digits;
    } else if (digits.length <= 4) {
      val = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    } else {
      val = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
    }
    
    setInputValue(val);
    
    // Only validate when we have a complete date
    if (val.length === 10) {
      validateAndParseDate(val);
    } else {
      setLocalError("");
    }
  };

  const handleBlur = () => {
    if (inputValue && inputValue.length > 0 && inputValue.length !== 10) {
      setLocalError("Please enter complete date in DD/MM/YYYY format");
    } else if (inputValue.length === 10) {
      validateAndParseDate(inputValue);
    }
  };

  return (
    <div>
      <div className="relative">
        <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          placeholder="DD/MM/YYYY"
          maxLength={10}
          className={cn(
            "pl-10 h-11",
            localError && "border-red-500 focus-visible:ring-red-500"
          )}
          data-testid="dob-input"
        />
      </div>
      {localError && (
        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          {localError}
        </p>
      )}
    </div>
  );
};

// Document Upload Field Component
const DocumentUploadField = ({ docType, label, description, document, uploading, onUpload, onDelete, disabled }) => {
  const inputRef = React.useRef(null);
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      onUpload(docType, file);
    }
    e.target.value = '';
  };
  
  const getFileIcon = (contentType) => {
    if (contentType?.startsWith('image/')) {
      return <Image className="w-5 h-5 text-blue-600" />;
    }
    return <FileText className="w-5 h-5 text-red-600" />;
  };
  
  return (
    <div className="border border-slate-200 rounded-lg p-4 bg-slate-50/50">
      <Label className="text-sm font-medium text-slate-700 mb-1 block">
        {label}
      </Label>
      <p className="text-xs text-slate-500 mb-3">{description}</p>
      
      {document ? (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-md p-3">
          <div className="flex items-center gap-2 min-w-0">
            {getFileIcon(document.content_type)}
            <span className="text-sm text-slate-700 truncate">{document.filename}</span>
            <CheckCircle className="w-4 h-4 text-teal-600 flex-shrink-0" />
          </div>
          {!disabled && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onDelete(docType)}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      ) : (
        <div>
          <input
            ref={inputRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || uploading}
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => inputRef.current?.click()}
            disabled={disabled || uploading}
            className="w-full h-20 border-dashed border-2 hover:border-teal-400 hover:bg-teal-50/50"
            data-testid={`upload-${docType}-btn`}
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Uploading...</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Upload className="w-5 h-5 text-slate-400" />
                <span className="text-sm text-slate-500">Click to upload</span>
              </div>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

const ApplicationFormPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  
  // Admin edit mode - get application ID from URL param
  const editApplicationId = searchParams.get("edit");
  const isAdminEditMode = editApplicationId && (user?.role === "admin" || user?.role === "secretariat");
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  
  // Document upload state
  const [documents, setDocuments] = useState({});
  const [uploadingDoc, setUploadingDoc] = useState(null);
  
  // Correction request state
  const [correctionDetails, setCorrectionDetails] = useState(null);
  const [applicationStatus, setApplicationStatus] = useState(null);
  
  // Target user for admin edit mode
  const [targetUserId, setTargetUserId] = useState(null);
  
  // Determine if application is editable based on status and user role
  // a. Admin/Secretariat can edit ALL applications
  // b. Applicants can edit when in correction_requested or needs_correction status
  const isAdminOrSecretariat = user?.role === "admin" || user?.role === "secretariat";
  const applicantEditableStatuses = ["draft", "pending_application", "needs_correction", "need_clarification", "correction_requested", null, undefined];
  const isEditable = isAdminOrSecretariat || applicantEditableStatuses.includes(applicationStatus);
  const isSubmitted = applicationStatus && !applicantEditableStatuses.includes(applicationStatus) && !isAdminOrSecretariat;

  const [formData, setFormData] = useState({
    // Personal Details
    date_of_birth: null,
    gender: "",
    email_display_permitted: false,
    phone_display_permitted: false,
    house_number: "",
    street_address: "",
    city: "",
    state: "",
    country: "India",
    pin_code: "",
    
    // Work Details
    designation: "",
    organization_name: "",
    work_address: "",
    work_state: "",
    work_country: "India",
    work_pin_code: "",
    
    // Academic Details
    graduation_course: "",
    graduation_university: "",
    graduation_year: "",
    pg_course: "",
    pg_university: "",
    pg_year: "",
    other_qualification: "",
    other_qualification_university: "",
    other_qualification_year: "",
    phd_course: "",
    phd_university: "",
    phd_year: "",
    
    // Professional Details
    years_of_experience: "",
    rci_status: "",
    rci_number: "",
    
    // Student Details
    course_studying: "",
    institution_name: "",
    expected_completion_date: "",
    
    // Interests
    areas_of_interest: "",
    
    // Terms
    terms_accepted: false,
  });

  const genderOptions = [
    { value: "female", label: "Female" },
    { value: "male", label: "Male" },
    { value: "other", label: "Other" },
    { value: "not_share", label: "Do not want to share" },
  ];

  const rciStatusOptions = [
    { value: "active", label: "Active" },
    { value: "not_active", label: "Not Active" },
    { value: "applied_for", label: "Applied for" },
    { value: "pre_crr", label: "Pre-CRR" },
    { value: "not_registered", label: "Not registered" },
    { value: "not_applicable", label: "Not Applicable" },
  ];

  const otherQualificationOptions = [
    { value: "dmp", label: "Diploma in Medical Psychology" },
    { value: "dmsp", label: "Diploma in Medical & Social Psychology" },
    { value: "mphil_msp", label: "M.Phil. Medical & Social Psychology" },
  ];

  const states = [
    "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
    "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand",
    "Karnataka", "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur",
    "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
    "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
    "Uttarakhand", "West Bengal"
  ];

  const isStudent = user?.membership_type === "student";

  useEffect(() => {
    // Load existing application if any
    if (isAdminEditMode) {
      fetchApplicationForAdmin();
    } else {
      fetchExistingApplication();
      fetchCorrectionDetails();
    }
    fetchUploadedDocuments();
  }, [editApplicationId]);

  // Fetch application for admin edit mode
  const fetchApplicationForAdmin = async () => {
    try {
      const response = await axios.get(`${API}/admin/applications/${editApplicationId}/details`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      if (response.data && response.data.application) {
        const app = response.data.application;
        setApplicationStatus(app.status);
        setTargetUserId(app.user_id);
        
        // Parse date_of_birth if it exists
        let parsedDob = null;
        if (app.personal_details?.date_of_birth) {
          const dobStr = app.personal_details.date_of_birth;
          parsedDob = parse(dobStr, "yyyy-MM-dd", new Date());
          if (!isValid(parsedDob)) {
            parsedDob = null;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          ...app.personal_details,
          date_of_birth: parsedDob,
          ...app.work_details,
          ...app.academic_details,
          ...app.professional_details,
          ...app.student_details,
          areas_of_interest: app.areas_of_interest || "",
          terms_accepted: app.terms_accepted || false,
        }));
        
        if (app.areas_of_interest) {
          setWordCount(app.areas_of_interest.trim().split(/\s+/).filter(Boolean).length);
        }
        
        // Fetch documents for target user
        fetchUploadedDocumentsForUser(app.user_id);
      }
    } catch (error) {
      console.error("Error fetching application for admin:", error);
      toast.error("Failed to load application");
    }
  };

  const fetchExistingApplication = async () => {
    try {
      const response = await axios.get(`${API}/membership/application`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      if (response.data && response.data.status !== "no_application") {
        const app = response.data;
        setApplicationStatus(app.status);
        
        // Parse date_of_birth if it exists
        let parsedDob = null;
        if (app.personal_details?.date_of_birth) {
          const dobStr = app.personal_details.date_of_birth;
          // Try to parse YYYY-MM-DD format from backend
          parsedDob = parse(dobStr, "yyyy-MM-dd", new Date());
          if (!isValid(parsedDob)) {
            parsedDob = null;
          }
        }
        
        setFormData(prev => ({
          ...prev,
          ...app.personal_details,
          date_of_birth: parsedDob,
          ...app.work_details,
          ...app.academic_details,
          ...app.professional_details,
          ...app.student_details,
          areas_of_interest: app.areas_of_interest || "",
          terms_accepted: app.terms_accepted || false,
        }));
        
        if (app.areas_of_interest) {
          setWordCount(app.areas_of_interest.trim().split(/\s+/).filter(Boolean).length);
        }
      }
    } catch (error) {
      console.error("Error fetching application:", error);
    }
  };

  const fetchUploadedDocumentsForUser = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/documents`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      const docs = {};
      (response.data.documents || []).forEach(doc => {
        docs[doc.type] = doc;
      });
      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching documents for user:", error);
    }
  };

  const fetchUploadedDocuments = async () => {
    try {
      const response = await axios.get(`${API}/member/documents`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      const docs = {};
      (response.data.documents || []).forEach(doc => {
        docs[doc.type] = doc;
      });
      setDocuments(docs);
    } catch (error) {
      console.error("Error fetching documents:", error);
    }
  };

  const fetchCorrectionDetails = async () => {
    try {
      const response = await axios.get(`${API}/member/correction-details`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      // Check for both correction statuses
      if (response.data.status === "needs_correction" || response.data.status === "correction_requested") {
        setCorrectionDetails(response.data);
      }
    } catch (error) {
      // Ignore 404 - no corrections needed
    }
  };

  const handleDocumentUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG, or PDF files are allowed");
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }
    
    setUploadingDoc(docType);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);
      
      const response = await axios.post(`${API}/upload/document`, formData, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("session_token")}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setDocuments(prev => ({
        ...prev,
        [docType]: {
          file_id: response.data.file_id,
          filename: file.name,
          type: docType,
          content_type: file.type
        }
      }));
      
      toast.success(`${getDocumentLabel(docType)} uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingDoc(null);
    }
  };

  const handleDeleteDocument = async (docType) => {
    const doc = documents[docType];
    if (!doc) return;
    
    try {
      await axios.delete(`${API}/upload/${doc.file_id}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      setDocuments(prev => {
        const newDocs = { ...prev };
        delete newDocs[docType];
        return newDocs;
      });
      
      toast.success("Document removed");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete document");
    }
  };

  const handleResubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/member/resubmit-application`, {}, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
      });
      
      setCorrectionDetails(null);
      setApplicationStatus("under_scrutiny");
      toast.success("Application resubmitted for review");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resubmit");
    } finally {
      setLoading(false);
    }
  };

  const getDocumentLabel = (docType) => {
    const labels = {
      photo: "Business Photograph",
      aadhaar_front: "Aadhaar Card (Front)",
      aadhaar_rear: "Aadhaar Card (Rear)",
      rci_certificate: "RCI Certificate",
      student_id: "Student ID / Institution Letter"
    };
    return labels[docType] || docType;
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError("");
  };

  const handleInterestsChange = (e) => {
    const text = e.target.value;
    const words = text.trim().split(/\s+/).filter(Boolean).length;
    
    if (words <= 100) {
      setFormData(prev => ({ ...prev, areas_of_interest: text }));
      setWordCount(words);
    }
  };

  const calculateAge = (dob) => {
    if (!dob) return "";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const validateForm = () => {
    // Date of Birth validation
    if (!formData.date_of_birth) {
      setError("Date of Birth is required");
      toast.error("Date of Birth is required");
      return false;
    }
    // Gender validation  
    if (!formData.gender) {
      setError("Gender is required");
      toast.error("Gender is required");
      return false;
    }
    // RCI Status validation (Part A - mandatory field enforcement)
    if (!formData.rci_status) {
      setError("RCI Registration Status is required");
      toast.error("RCI Registration Status is required");
      return false;
    }
    // Declaration/Terms validation
    if (!formData.terms_accepted) {
      setError("You must accept the declaration to submit your application");
      toast.error("You must accept the declaration to submit");
      return false;
    }
    return true;
  };

  const handleSaveDraft = async () => {
    // P1-01: Use distinct draft save endpoint - DOES NOT submit application
    setSaving(true);
    setError("");

    try {
      const payload = buildPayload();
      payload.terms_accepted = false;
      
      // Admin edit mode uses different endpoint
      if (isAdminEditMode) {
        await axios.put(`${API}/admin/applications/${editApplicationId}`, payload, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success("Application updated successfully!");
      } else {
        // Use NEW draft-specific endpoint
        await axios.post(`${API}/membership/application/draft`, payload, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        // Refetch from server to get authoritative state
        await fetchExistingApplication();
        toast.success("Draft saved successfully!");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const buildPayload = () => {
    return {
      personal_details: {
        date_of_birth: formData.date_of_birth ? format(formData.date_of_birth, "yyyy-MM-dd") : null,
        gender: formData.gender,
        email_display_permitted: formData.email_display_permitted,
        phone_display_permitted: formData.phone_display_permitted,
        house_number: formData.house_number,
        street_address: formData.street_address,
        city: formData.city,
        state: formData.state,
        country: formData.country,
        pin_code: formData.pin_code,
      },
      work_details: {
        designation: formData.designation,
        organization_name: formData.organization_name,
        work_address: formData.work_address,
        work_state: formData.work_state,
        work_country: formData.work_country,
        work_pin_code: formData.work_pin_code,
      },
      academic_details: {
        graduation_course: formData.graduation_course,
        graduation_university: formData.graduation_university,
        graduation_year: formData.graduation_year ? parseInt(formData.graduation_year) : null,
        pg_course: formData.pg_course,
        pg_university: formData.pg_university,
        pg_year: formData.pg_year ? parseInt(formData.pg_year) : null,
        other_qualification: formData.other_qualification,
        other_qualification_university: formData.other_qualification_university,
        other_qualification_year: formData.other_qualification_year ? parseInt(formData.other_qualification_year) : null,
        phd_course: formData.phd_course,
        phd_university: formData.phd_university,
        phd_year: formData.phd_year ? parseInt(formData.phd_year) : null,
      },
      professional_details: {
        years_of_experience: formData.years_of_experience ? parseInt(formData.years_of_experience) : null,
        rci_status: formData.rci_status,
        rci_number: formData.rci_number,
      },
      student_details: isStudent ? {
        course_studying: formData.course_studying,
        institution_name: formData.institution_name,
        expected_completion_date: formData.expected_completion_date,
      } : null,
      areas_of_interest: formData.areas_of_interest,
      terms_accepted: formData.terms_accepted,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setError("");

    try {
      const payload = buildPayload();
      
      // Admin edit mode uses PUT endpoint
      if (isAdminEditMode) {
        await axios.put(`${API}/admin/applications/${editApplicationId}`, payload, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        toast.success("Application updated successfully!");
        setTimeout(() => navigate("/admin/applications"), 2000);
      } else {
        // P1-01: Use NEW submit-specific endpoint - DISTINCT from Save Draft
        await axios.post(`${API}/membership/application/submit`, payload, {
          withCredentials: true,
          headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
        });
        setSuccess(true);
        toast.success("Application submitted successfully!");
        setTimeout(() => navigate("/dashboard"), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to submit application");
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border border-slate-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-teal-700" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-slate-900 mb-2">
              Application Submitted!
            </h2>
            <p className="text-slate-600">
              Your application has been submitted for review. We&apos;ll notify you once the committee reviews it.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="application-form-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Membership Application
          </h1>
          <p className="text-slate-600">
            Complete your application by filling in all required details
          </p>
        </div>

        {/* Admin Edit Mode Banner */}
        {isAdminEditMode && (
          <Alert className="mb-6 bg-purple-50 border-purple-200">
            <Shield className="h-5 w-5 text-purple-600" />
            <AlertDescription className="ml-2 text-purple-800">
              <strong>Admin Edit Mode</strong> — You are editing application <code className="bg-purple-100 px-1 rounded">{editApplicationId}</code>. 
              Changes will be saved directly to the applicant's record.
            </AlertDescription>
          </Alert>
        )}

        {/* Correction Request Alert */}
        {correctionDetails && (applicationStatus === "needs_correction" || applicationStatus === "correction_requested") && (
          <Alert className="mb-6 border-amber-300 bg-amber-50">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            <AlertDescription className="ml-2">
              <div className="font-semibold text-amber-800 mb-2">
                Corrections Requested
              </div>
              <div className="text-amber-700 whitespace-pre-line mb-4">
                {correctionDetails.correction_comments}
              </div>
              <p className="text-sm text-amber-600 mb-3">
                Requested on: {new Date(correctionDetails.correction_requested_at).toLocaleDateString()}
              </p>
              <p className="text-sm text-slate-600">
                Please update your application and documents as requested, then click &quot;Resubmit for Review&quot; below.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Read-Only Banner for submitted applications (only for non-admin applicants) */}
        {isSubmitted && !isAdminEditMode && (
          <Alert className="mb-6 bg-blue-50 border-blue-200">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800">
              <strong>Application Under Review</strong> — Your application has been submitted and is currently being processed. 
              You cannot make changes at this time. {applicationStatus === "under_scrutiny" && "The Secretariat is reviewing your application."}
              {applicationStatus === "under_committee_review" && "Your application is under committee review."}
              {applicationStatus === "approved_pending_payment" && "Your application has been approved. Please complete the payment."}
            </AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <fieldset disabled={isSubmitted}>
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Section 1: Personal Details */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-teal-700" />
                Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Date of Birth */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Date of Birth *
                  </Label>
                  <DateInput
                    value={formData.date_of_birth}
                    onChange={(date) => handleChange("date_of_birth", date)}
                  />
                </div>

                {/* Age (Auto-calculated) */}
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Age
                  </Label>
                  <Input
                    value={calculateAge(formData.date_of_birth)}
                    disabled
                    className="h-11 bg-slate-50"
                  />
                </div>
              </div>

              {/* Gender */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Gender *
                </Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) => handleChange("gender", value)}
                >
                  <SelectTrigger className="h-11" data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    {genderOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Privacy Permissions */}
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="email_display"
                    checked={formData.email_display_permitted}
                    onCheckedChange={(checked) => handleChange("email_display_permitted", checked)}
                    data-testid="email-display-checkbox"
                  />
                  <Label htmlFor="email_display" className="text-sm font-normal">
                    Permit display of email in the public directory profile
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="phone_display"
                    checked={formData.phone_display_permitted}
                    onCheckedChange={(checked) => handleChange("phone_display_permitted", checked)}
                    data-testid="phone-display-checkbox"
                  />
                  <Label htmlFor="phone_display" className="text-sm font-normal">
                    Permit display of phone/WhatsApp number in the public directory profile
                  </Label>
                </div>
              </div>

              <Separator />

              {/* Residential Address */}
              <div>
                <h4 className="font-medium text-slate-900 mb-4">Residential Address</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      House/Apartment Number
                    </Label>
                    <Input
                      value={formData.house_number}
                      onChange={(e) => handleChange("house_number", e.target.value)}
                      className="h-11"
                      data-testid="house-number"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Street Address
                    </Label>
                    <Input
                      value={formData.street_address}
                      onChange={(e) => handleChange("street_address", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      City
                    </Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      State
                    </Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => handleChange("state", value)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {states.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Country
                    </Label>
                    <Input
                      value={formData.country}
                      onChange={(e) => handleChange("country", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      PIN Code
                    </Label>
                    <Input
                      value={formData.pin_code}
                      onChange={(e) => handleChange("pin_code", e.target.value)}
                      className="h-11"
                      maxLength={6}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Work Details */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-teal-700" />
                Current Work Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Designation
                  </Label>
                  <Input
                    value={formData.designation}
                    onChange={(e) => handleChange("designation", e.target.value)}
                    className="h-11"
                    data-testid="designation"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Organization Name
                  </Label>
                  <Input
                    value={formData.organization_name}
                    onChange={(e) => handleChange("organization_name", e.target.value)}
                    className="h-11"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Work Address
                  </Label>
                  <Textarea
                    value={formData.work_address}
                    onChange={(e) => handleChange("work_address", e.target.value)}
                    rows={2}
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    State
                  </Label>
                  <Select
                    value={formData.work_state}
                    onValueChange={(value) => handleChange("work_state", value)}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state} value={state}>
                          {state}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    PIN Code
                  </Label>
                  <Input
                    value={formData.work_pin_code}
                    onChange={(e) => handleChange("work_pin_code", e.target.value)}
                    className="h-11"
                    maxLength={6}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Academic Credentials */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-teal-700" />
                Academic Credentials
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Graduation */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Graduation</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Course Name
                    </Label>
                    <Input
                      value={formData.graduation_course}
                      onChange={(e) => handleChange("graduation_course", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      University
                    </Label>
                    <Input
                      value={formData.graduation_university}
                      onChange={(e) => handleChange("graduation_university", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Year of Passing
                    </Label>
                    <Input
                      type="number"
                      value={formData.graduation_year}
                      onChange={(e) => handleChange("graduation_year", e.target.value)}
                      className="h-11"
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Post Graduation */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Post Graduation</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Course Name
                    </Label>
                    <Input
                      value={formData.pg_course}
                      onChange={(e) => handleChange("pg_course", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      University
                    </Label>
                    <Input
                      value={formData.pg_university}
                      onChange={(e) => handleChange("pg_university", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Year of Passing
                    </Label>
                    <Input
                      type="number"
                      value={formData.pg_year}
                      onChange={(e) => handleChange("pg_year", e.target.value)}
                      className="h-11"
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Other Qualification */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Other Professional Qualification</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Qualification
                    </Label>
                    <Select
                      value={formData.other_qualification}
                      onValueChange={(value) => handleChange("other_qualification", value)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {otherQualificationOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      University
                    </Label>
                    <Input
                      value={formData.other_qualification_university}
                      onChange={(e) => handleChange("other_qualification_university", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Year of Passing
                    </Label>
                    <Input
                      type="number"
                      value={formData.other_qualification_year}
                      onChange={(e) => handleChange("other_qualification_year", e.target.value)}
                      className="h-11"
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
              </div>

              <Separator />

              {/* Ph.D. */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Ph.D. / Other Course</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Course Name
                    </Label>
                    <Input
                      value={formData.phd_course}
                      onChange={(e) => handleChange("phd_course", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      University
                    </Label>
                    <Input
                      value={formData.phd_university}
                      onChange={(e) => handleChange("phd_university", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Year of Passing
                    </Label>
                    <Input
                      type="number"
                      value={formData.phd_year}
                      onChange={(e) => handleChange("phd_year", e.target.value)}
                      className="h-11"
                      min="1950"
                      max={new Date().getFullYear()}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 4: Professional Details */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Award className="w-5 h-5 text-teal-700" />
                Professional Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    Years of Experience
                  </Label>
                  <Input
                    type="number"
                    value={formData.years_of_experience}
                    onChange={(e) => handleChange("years_of_experience", e.target.value)}
                    className="h-11"
                    min="0"
                    data-testid="experience"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                    RCI Registration Status
                  </Label>
                  <Select
                    value={formData.rci_status}
                    onValueChange={(value) => handleChange("rci_status", value)}
                  >
                    <SelectTrigger className="h-11" data-testid="rci-status">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {rciStatusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {formData.rci_status && 
                 formData.rci_status !== "not_registered" && 
                 formData.rci_status !== "not_applicable" && (
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      RCI Registration Number
                    </Label>
                    <Input
                      value={formData.rci_number}
                      onChange={(e) => handleChange("rci_number", e.target.value)}
                      className="h-11"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Section 5: Student Details (Conditional) */}
          {isStudent && (
            <Card className="border border-slate-200 mb-6">
              <CardHeader>
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-teal-700" />
                  Student Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Course Studying In
                    </Label>
                    <Input
                      value={formData.course_studying}
                      onChange={(e) => handleChange("course_studying", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Name of Institution
                    </Label>
                    <Input
                      value={formData.institution_name}
                      onChange={(e) => handleChange("institution_name", e.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Expected Completion Date
                    </Label>
                    <Input
                      type="month"
                      value={formData.expected_completion_date}
                      onChange={(e) => handleChange("expected_completion_date", e.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Section 6: Areas of Interest */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <FileText className="w-5 h-5 text-teal-700" />
                Areas of Professional Interest
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-1.5 block">
                  Describe your areas of interest (max 100 words)
                </Label>
                <Textarea
                  value={formData.areas_of_interest}
                  onChange={handleInterestsChange}
                  rows={4}
                  placeholder="Share your professional interests, research areas, or specializations..."
                  data-testid="interests"
                />
                <p className={`text-sm mt-2 ${wordCount > 90 ? 'text-amber-600' : 'text-slate-500'}`}>
                  {wordCount}/100 words
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Section 7: Document Uploads */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Upload className="w-5 h-5 text-teal-700" />
                Supporting Documents
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-slate-600">
                Please upload the following documents. Accepted formats: JPEG, PNG, PDF. Maximum size: 5MB per file.
              </p>
              
              {/* Document Upload Items */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Photo */}
                <DocumentUploadField
                  docType="photo"
                  label="Recent Business Photograph *"
                  description="Passport size photo with white background"
                  document={documents.photo}
                  uploading={uploadingDoc === "photo"}
                  onUpload={handleDocumentUpload}
                  onDelete={handleDeleteDocument}
                  disabled={!isEditable}
                />
                
                {/* Aadhaar Front */}
                <DocumentUploadField
                  docType="aadhaar_front"
                  label="Aadhaar Card (Front) *"
                  description="Clear scan of Aadhaar front side"
                  document={documents.aadhaar_front}
                  uploading={uploadingDoc === "aadhaar_front"}
                  onUpload={handleDocumentUpload}
                  onDelete={handleDeleteDocument}
                  disabled={!isEditable}
                />
                
                {/* Aadhaar Rear */}
                <DocumentUploadField
                  docType="aadhaar_rear"
                  label="Aadhaar Card (Rear) *"
                  description="Clear scan of Aadhaar back side"
                  document={documents.aadhaar_rear}
                  uploading={uploadingDoc === "aadhaar_rear"}
                  onUpload={handleDocumentUpload}
                  onDelete={handleDeleteDocument}
                  disabled={!isEditable}
                />
                
                {/* RCI Certificate (conditional) */}
                {formData.rci_status && formData.rci_status !== "not_registered" && formData.rci_status !== "not_applicable" && (
                  <DocumentUploadField
                    docType="rci_certificate"
                    label="RCI Registration Certificate"
                    description="Copy of your RCI certificate"
                    document={documents.rci_certificate}
                    uploading={uploadingDoc === "rci_certificate"}
                    onUpload={handleDocumentUpload}
                    onDelete={handleDeleteDocument}
                    disabled={!isEditable}
                  />
                )}
                
                {/* Student ID (conditional) */}
                {isStudent && (
                  <DocumentUploadField
                    docType="student_id"
                    label="Student ID / Institution Letter *"
                    description="Valid student ID or authentication letter"
                    document={documents.student_id}
                    uploading={uploadingDoc === "student_id"}
                    onUpload={handleDocumentUpload}
                    onDelete={handleDeleteDocument}
                    disabled={!isEditable}
                  />
                )}
                
                {/* Part B: Qualification Certificate (optional) */}
                <DocumentUploadField
                  docType="qualification_certificate"
                  label="Qualification Certificate"
                  description="Degree/diploma certificate (optional)"
                  document={documents.qualification_certificate}
                  uploading={uploadingDoc === "qualification_certificate"}
                  onUpload={handleDocumentUpload}
                  onDelete={handleDeleteDocument}
                  disabled={!isEditable}
                />
              </div>
            </CardContent>
          </Card>

          {/* Section 8: Declaration */}
          <Card className="border border-slate-200 mb-6">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Declaration</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 p-4 rounded-lg mb-4 text-sm text-slate-700 leading-relaxed">
                <p className="mb-2">
                  I hereby certify that the particulars given above are true to the best of my knowledge.
                </p>
                <p className="mb-2">
                  I am interested in the aims and objectives of CPSI and undertake to abide by the rules 
                  and regulations during the tenure of my relationship.
                </p>
                <p className="mb-2">
                  I am attaching the copies of my professional degree certificates to substantiate the 
                  qualification required for the membership category applied for.
                </p>
                <p>
                  I agree to receive updates or notification regarding the activities or events of CPSI.
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="terms"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => handleChange("terms_accepted", checked)}
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms" className="text-sm font-medium">
                  I accept the Terms & Conditions *
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-end">
            {isSubmitted && !isAdminEditMode ? (
              /* Read-only state - show status instead of buttons */
              <div className="flex items-center gap-2 text-blue-700 bg-blue-50 px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5" />
                <span className="font-medium">Application Under Review</span>
              </div>
            ) : (
              <>
                <Button 
                  type="button"
                  variant="outline"
                  onClick={handleSaveDraft}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? "Saving..." : isAdminEditMode ? "Save Changes" : "Save Draft"}
                </Button>
                
                {isAdminEditMode ? (
                  <Button 
                    type="submit"
                    className="bg-purple-600 text-white hover:bg-purple-700"
                    disabled={loading}
                    data-testid="admin-save-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {loading ? "Saving..." : "Save & Return"}
                  </Button>
                ) : (applicationStatus === "needs_correction" || applicationStatus === "correction_requested") ? (
                  <Button 
                    type="button"
                    onClick={handleResubmit}
                    className="bg-amber-600 text-white hover:bg-amber-700"
                    disabled={loading || !formData.terms_accepted}
                    data-testid="resubmit-application-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? "Resubmitting..." : "Resubmit for Review"}
                  </Button>
                ) : (
                  <Button 
                    type="submit"
                    className="bg-slate-900 text-white hover:bg-slate-800"
                    disabled={loading || !formData.terms_accepted}
                    data-testid="submit-application-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {loading ? "Submitting..." : "Submit for Review"}
                  </Button>
                )}
              </>
            )}
          </div>
          </fieldset>
        </form>
      </div>
    </div>
  );
};

export default ApplicationFormPage;
